import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { useT } from "@transifex/react";
import { RendererProps } from "./index";
import { TextInput } from "./renderer-styles";

const LBS_PER_KG = 2.20462;

type Unit = "lbs" | "kg";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 3px;
  width: fit-content;
`;

const ToggleButton = styled.button<{ $active?: boolean }>`
  padding: 6px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  font-family: "Dosis", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ $active }) => ($active ? "white" : "transparent")};
  color: ${({ $active }) => ($active ? "#0066cc" : "#64748b")};
  box-shadow: ${({ $active }) => ($active ? "0 1px 3px rgba(0,0,0,0.1)" : "none")};
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UnitLabel = styled.span`
  font-size: 16px;
  color: #4a4d5e;
  font-weight: 600;
`;

function lbsToKg(lbs: number): number {
  return Math.round(lbs / LBS_PER_KG);
}

function kgToLbs(kg: number): number {
  return Math.round(kg * LBS_PER_KG);
}

const WeightInputRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {
  const t = useT();
  const minLbs = question.min ?? 0;
  const maxLbs = question.max ?? 999;

  const initialLbs = answer?.value != null ? Number(answer.value) : null;

  const [unit, setUnit] = useState<Unit>("lbs");
  const [displayValue, setDisplayValue] = useState<string>(
    initialLbs != null ? String(initialLbs) : ""
  );

  const validate = useCallback(
    (lbs: number) => !isNaN(lbs) && lbs >= minLbs && lbs <= maxLbs,
    [minLbs, maxLbs]
  );

  const handleChange = useCallback(
    (raw: string) => {
      setDisplayValue(raw);

      if (raw === "") {
        onAnswer("", !question.required);
        return;
      }

      const num = Number(raw);
      if (isNaN(num)) {
        onAnswer("", false);
        return;
      }

      const lbs = unit === "kg" ? kgToLbs(num) : num;
      onAnswer(lbs, validate(lbs));
    },
    [unit, onAnswer, question.required, validate]
  );

  const handleUnitToggle = useCallback(
    (newUnit: Unit) => {
      if (newUnit === unit) return;

      setUnit(newUnit);
      if (displayValue === "") return;

      const num = Number(displayValue);
      if (isNaN(num)) return;

      if (newUnit === "kg") {
        setDisplayValue(String(lbsToKg(num)));
      } else {
        setDisplayValue(String(kgToLbs(num)));
      }
    },
    [unit, displayValue]
  );

  return (
    <Wrapper>
      <ToggleRow>
        <ToggleButton
          type="button"
          $active={unit === "lbs"}
          onClick={() => handleUnitToggle("lbs")}
        >
          {t("lbs")}
        </ToggleButton>
        <ToggleButton
          type="button"
          $active={unit === "kg"}
          onClick={() => handleUnitToggle("kg")}
        >
          {t("kg")}
        </ToggleButton>
      </ToggleRow>
      <InputRow>
        <TextInput
          type="number"
          value={displayValue}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={question.placeholder || ""}
          min={unit === "kg" ? lbsToKg(minLbs) : minLbs}
          max={unit === "kg" ? lbsToKg(maxLbs) : maxLbs}
          style={{ maxWidth: 160 }}
        />
        <UnitLabel>{unit}</UnitLabel>
      </InputRow>
    </Wrapper>
  );
};

export default WeightInputRenderer;
